import type { FirebaseServices } from './app.ts';

// "Active now" (privacy model): each person writes only their own status; the
// rules show it only to people they already chat with, unless they turn it off.
export function trackPresence(services: FirebaseServices, uid: string, showActive: boolean): () => void {
  let stopped = false;
  let cleanup = () => {};
  void (async () => {
    const [{ onDisconnect, ref, set, onValue, serverTimestamp }, database] = await Promise.all([import('firebase/database'), services.database()]);
    if (stopped) return;
    const mine = ref(database, `active_status/${uid}`);
    const connected = ref(database, '.info/connected');
    const unsubscribe = onValue(connected, snapshot => {
      if (snapshot.val() !== true) return;
      void onDisconnect(mine).set({ online: false, lastActive: serverTimestamp(), showActive }).then(() => set(mine, { online: true, lastActive: Date.now(), showActive }));
    });
    cleanup = () => {
      unsubscribe();
      void set(mine, { online: false, lastActive: Date.now(), showActive }).catch(() => undefined);
    };
  })().catch(() => undefined);
  return () => {
    stopped = true;
    cleanup();
  };
}

export function watchActive(services: FirebaseServices, uid: string, listener: (active: { online: boolean; lastActive: number } | null) => void): () => void {
  let stop = () => {};
  let stopped = false;
  void (async () => {
    const [{ onValue, ref }, database] = await Promise.all([import('firebase/database'), services.database()]);
    if (stopped) return;
    stop = onValue(ref(database, `active_status/${uid}`), snapshot => {
      const value = snapshot.val() as { online?: boolean; lastActive?: number } | null;
      listener(value ? { online: value.online === true, lastActive: Number(value.lastActive || 0) } : null);
    }, () => listener(null));
  })().catch(() => listener(null));
  return () => {
    stopped = true;
    stop();
  };
}

export async function readShowActive(services: FirebaseServices, uid: string): Promise<boolean> {
  const [{ get, ref }, database] = await Promise.all([import('firebase/database'), services.database()]);
  const value = (await get(ref(database, `active_status/${uid}/showActive`))).val();
  return value !== false;
}

// Typing in a guild (guild_typing) or a direct message (dm_typing): members
// write the time they last typed; others see who typed in the last few
// seconds. The rules allow the guild's or the thread's members only.
type TypingRoot = 'guild_typing' | 'dm_typing';

export function watchTyping(services: FirebaseServices, id: string, listener: (typing: Record<string, number>) => void, root: TypingRoot = 'guild_typing'): () => void {
  let stop = () => {};
  let stopped = false;
  void (async () => {
    const [{ onValue, ref }, database] = await Promise.all([import('firebase/database'), services.database()]);
    if (stopped) return;
    stop = onValue(ref(database, `${root}/${id}`), snapshot => listener((snapshot.val() as Record<string, number> | null) ?? {}), () => listener({}));
  })().catch(() => listener({}));
  return () => {
    stopped = true;
    stop();
  };
}

export async function markTyping(services: FirebaseServices, id: string, uid: string, root: TypingRoot = 'guild_typing'): Promise<void> {
  const [{ ref, set }, database] = await Promise.all([import('firebase/database'), services.database()]);
  await set(ref(database, `${root}/${id}/${uid}`), Date.now());
}
